import React from "react";
import { useParams, Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { CtaBlock } from "@/components/CtaBlock";
import NotFound from "@/pages/public/NotFound";
import { Button } from "@/components/ui/button";
import { MapPin, Phone, Clock, Navigation, Check } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export default function DealerDetail() {
  const { slug } = useParams();
  const { data: d, isLoading, isError } = useQuery({
    queryKey: ["dealer", slug], queryFn: () => api.get(`/dealers/${slug}`).then((r) => r.data), retry: false,
  });
  if (isLoading) return <div className="max-w-4xl mx-auto px-4 py-20 text-slate-500">Caricamento...</div>;
  if (isError || !d) return <NotFound />;

  const jsonLd = {
    "@context": "https://schema.org", "@type": "LocalBusiness", name: d.dealer_name,
    address: { "@type": "PostalAddress", streetAddress: d.address, addressLocality: d.city, addressRegion: d.province, postalCode: d.postal_code, addressCountry: "IT" },
    telephone: d.telephone, geo: { "@type": "GeoCoordinates", latitude: d.latitude, longitude: d.longitude },
    openingHours: d.opening_hours,
  };

  return (
    <>
      <Seo title={`${d.dealer_name} — ${d.city}`} description={d.dealer_description} jsonLd={jsonLd} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6">
        <Breadcrumbs items={[{ label: "Rivenditori", path: "/rivenditori" }, { label: d.city }, { label: d.dealer_name }]} />
      </div>
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-6">
        {d.photos?.[0] && <img src={d.photos[0]} alt={d.dealer_name} loading="lazy" className="rounded-3xl w-full h-[300px] object-cover shadow-lg mb-8" />}
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight" style={{ fontFamily: "Outfit" }}>{d.dealer_name}</h1>
        <p className="mt-2 text-slate-500 text-lg">{d.city} ({d.province}) · {d.region}</p>
        <p className="mt-5 text-lg text-slate-600 leading-relaxed">{d.dealer_description}</p>

        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          <div className="rounded-2xl bg-white border border-slate-200 p-5 space-y-3 text-slate-700">
            <p className="flex items-start gap-2"><MapPin size={18} className="mt-0.5 text-emerald-700 shrink-0" /> {d.address}</p>
            <p className="flex items-center gap-2"><Phone size={18} className="text-emerald-700" /> <a href={`tel:${d.telephone}`} className="hover:text-emerald-800">{d.telephone}</a></p>
            <p className="flex items-start gap-2"><Clock size={18} className="mt-0.5 text-emerald-700 shrink-0" /> {d.opening_hours}</p>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-5">
            <h3 className="font-bold text-slate-900 mb-3" style={{ fontFamily: "Outfit" }}>Servizi</h3>
            <ul className="space-y-2">{(d.services || []).map((s) => <li key={s} className="flex items-center gap-2 text-slate-700"><Check size={16} className="text-emerald-700" /> {s}</li>)}</ul>
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <Link to="/prenota-prova" className="flex-1"><Button className="w-full h-13 h-12 font-bold text-white rounded-full" style={{ backgroundColor: "var(--sc-amber)" }} data-testid="dealer-prenota">Prenota una prova qui</Button></Link>
          <a href={`https://www.google.com/maps/search/?api=1&query=${d.latitude},${d.longitude}`} target="_blank" rel="noreferrer" className="flex-1">
            <Button variant="outline" className="w-full h-12 font-bold rounded-full gap-2 border-2"><Navigation size={18} /> Come arrivare</Button>
          </a>
        </div>
      </section>
      <section className="max-w-4xl mx-auto px-4 sm:px-6 my-14"><CtaBlock variant="local" /></section>
    </>
  );
}
